import React, { useState } from "react";
import { Home, CalendarCheck, CalendarPlus, HelpCircle, Menu, LogOut } from "lucide-react";
import SelectorIdioma from "./SelectorIdioma";
import { useIdioma } from "../context/IdiomaContext";
import { colorTextoLegible } from "../utils/contraste";

const SidebarShell = ({
  usuario,
  onLogout,
  vistaActual,
  onNavegar,
  onVolver,
  colorAcento = "#1e293b",
  fondo,
  children,
}) => {
  const { t } = useIdioma();
  const [abierto, setAbierto] = useState(false);
  const colorTextoSidebar = colorTextoLegible(colorAcento);
  const colorTextoContenido = colorTextoLegible(fondo || "#ffffff");
  const clarito = colorTextoContenido === "#ffffff";

  const items = [
    { id: "inicio", label: t("portal_inicio"), icon: <Home size={19} /> },
    { id: "misCitas", label: t("portal_mis_citas"), icon: <CalendarCheck size={19} /> },
    { id: "servicios", label: t("portal_nueva_cita"), icon: <CalendarPlus size={19} /> },
    { id: "ayuda", label: t("portal_ayuda"), icon: <HelpCircle size={19} /> },
  ];

  // Estas vistas cuentan como "Nueva cita" para resaltar el ítem correcto
  const vistasReserva = ["servicios", "colegio", "cda", "kindergarten", "horarios", "reserva"];
  const activo = (id) => (id === "servicios" ? vistasReserva.includes(vistaActual) : vistaActual === id);

  const iniciales = (usuario?.nombre || "?")
    .split(" ")
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");

  const navegarYCerrar = (id) => {
    onNavegar(id);
    setAbierto(false);
  };

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: fondo }}>
      {/* FONDO OSCURO MÓVIL */}
      {abierto && (
        <div
          onClick={() => setAbierto(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 40 }}
          className="portal-backdrop"
        />
      )}

      {/* MENÚ LATERAL */}
      <aside
        className="portal-sidebar"
        style={{
          width: 260,
          background: colorAcento,
          color: colorTextoSidebar,
          display: "flex",
          flexDirection: "column",
          position: "fixed",
          top: 0,
          left: 0,
          height: "100vh",
          zIndex: 50,
          transform: abierto ? "translateX(0)" : undefined,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "20px 18px", borderBottom: `1px solid ${colorTextoSidebar}22` }}>
          <img src="/logo.png" alt="Logo" style={{ height: 34, borderRadius: 6 }} />
          <strong style={{ fontSize: 15, lineHeight: 1.2 }}>{t("panel_titulo")}</strong>
        </div>

        <div style={{ padding: 14, borderBottom: `1px solid ${colorTextoSidebar}22` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                background: `${colorTextoSidebar}22`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: 13,
                flexShrink: 0,
              }}
            >
              {iniciales}
            </div>
            <div style={{ minWidth: 0 }}>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {usuario?.nombre}
              </p>
              <p style={{ margin: 0, fontSize: 11, opacity: 0.8 }}>{t("portal_padre_familia")}</p>
            </div>
          </div>
          <button
            onClick={onLogout}
            style={{
              width: "100%",
              padding: "9px 12px",
              borderRadius: 10,
              border: "none",
              background: "#ef4444",
              color: "white",
              fontWeight: 700,
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            {t("panel_cerrar_sesion")}
          </button>
        </div>

        <nav style={{ flex: 1, padding: 14, display: "flex", flexDirection: "column", gap: 4 }}>
          {items.map((item) => (
            <button
              key={item.id}
              onClick={() => navegarYCerrar(item.id)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "11px 14px",
                borderRadius: 10,
                border: "none",
                background: activo(item.id) ? `${colorTextoSidebar}22` : "transparent",
                color: colorTextoSidebar,
                fontWeight: activo(item.id) ? 700 : 500,
                fontSize: 14,
                cursor: "pointer",
                textAlign: "left",
              }}
            >
              {item.icon}
              {item.label}
            </button>
          ))}
        </nav>
      </aside>

      {/* CONTENIDO */}
      <div className="portal-contenido" style={{ flex: 1, marginLeft: 260, display: "flex", flexDirection: "column", minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 20px" }}>
          <button
            onClick={() => setAbierto(true)}
            className="portal-menu-btn"
            style={{ display: "none", background: "transparent", border: "none", cursor: "pointer", color: colorTextoContenido }}
          >
            <Menu size={24} />
          </button>

          {onVolver && (
            <button
              onClick={onVolver}
              style={{
                background: clarito ? "rgba(255,255,255,0.15)" : "rgba(15,23,42,0.08)",
                color: colorTextoContenido,
                border: `1px solid ${clarito ? "rgba(255,255,255,0.4)" : "rgba(15,23,42,0.25)"}`,
                borderRadius: 8,
                padding: "7px 14px",
                fontWeight: 600,
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              {t("panel_regresar")}
            </button>
          )}

          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 14 }}>
            <button
              onClick={onLogout}
              className="portal-logout-mobile"
              style={{
                display: "none",
                alignItems: "center",
                gap: 6,
                background: "transparent",
                border: "none",
                cursor: "pointer",
                color: colorTextoContenido,
                opacity: 0.8,
                fontSize: 13,
                fontWeight: 600,
                padding: "6px 2px",
              }}
            >
              <LogOut size={16} />
              {t("panel_cerrar_sesion")}
            </button>

            <SelectorIdioma />
          </div>
        </div>

        <div style={{ flex: 1, padding: "0 20px 30px" }}>{children}</div>
      </div>

      <style>
        {`
          @media (max-width: 860px) {
            .portal-sidebar {
              transform: translateX(-100%);
              transition: transform 0.2s ease;
            }
            .portal-contenido {
              margin-left: 0 !important;
            }
            .portal-menu-btn {
              display: inline-flex !important;
            }
            .portal-logout-mobile {
              display: inline-flex !important;
            }
            .portal-backdrop {
              display: block;
            }
          }
          @media (min-width: 861px) {
            .portal-backdrop {
              display: none;
            }
          }
        `}
      </style>
    </div>
  );
};

export default SidebarShell;
